import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableHead, TableBody, TableRow, TableCell, Typography, Chip, Button,
  Grid, MenuItem, TextField, Drawer, Stack, Divider, IconButton, CircularProgress, Checkbox, Tooltip,
  FormControlLabel,
} from '@mui/material';
import { Add, Close, Delete as DeleteIcon, Edit as EditIcon, Payment as PaymentIcon, Print, Undo } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../../api/client';
import { PageHeader, PageActions } from '../../components/common/PageHeader';
import { StatusChip } from '../../components/common/StatusChip';
import { DataTable } from '../../components/common/DataTable';
import { DateField } from '../../components/fields/DateField';
import { printInvoice } from '../../utils/printInvoice';
import { printCreditNote } from '../../utils/printCreditNote';
import { useAuthStore } from '../../store/auth.store';
import { PermissionGate } from '../../components/auth/PermissionGate';
import { DeleteBillDialog } from '../../components/sales/DeleteBillDialog';
import { PaymentLegFields, usePaymentOptions } from '../../components/sales/PaymentLegFields';
import { LegMeta, blankMeta, legMetaPayload, legMetaError } from '../../utils/paymentLegMeta';

import { formatLocalDate } from '../../utils/date';
function fmt(n: number) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// ─── Invoice Detail Drawer (with Record Payment) ───────────────────────────────

function RecordPaymentForm({ invoiceId, balanceDue, branchId }: { invoiceId: string; balanceDue: number; branchId?: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  // Provider / terminal choices for the invoice's own branch; the server re-validates whatever is sent.
  const { options: payOptions, isLoading: optionsLoading, isError: optionsError } = usePaymentOptions(branchId);
  const [meta, setMeta] = useState<LegMeta>(blankMeta());
  const [showMetaErrors, setShowMetaErrors] = useState(false);
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      paymentDate: new Date().toISOString().split('T')[0],
      amount: balanceDue,
      mode: 'CASH',
      referenceNo: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: any) => apiPost(`/sales/invoices/${invoiceId}/payment`, {
      ...data, paymentDate: new Date(data.paymentDate), amount: Number(data.amount), ...legMetaPayload(data.mode, meta, payOptions),
    }),
    onSuccess: () => {
      setMeta(blankMeta()); setShowMetaErrors(false);
      qc.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
      qc.invalidateQueries({ queryKey: ['sales-invoices'] });
      reset();
      enqueueSnackbar('Payment recorded', { variant: 'success' });
    },
    onError: (e: any) => enqueueSnackbar(e.response?.data?.error ?? 'Failed to record payment', { variant: 'error' }),
  });

  return (
    <form onSubmit={handleSubmit((d) => { setShowMetaErrors(true); if (legMetaError(d.mode, meta, payOptions)) return; mutation.mutate(d); })}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Controller name="paymentDate" control={control}
          render={({ field }) => (
            <TextField {...field} size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
          )} />
        <Controller name="amount" control={control} rules={{ required: true, min: 0.01 }}
          render={({ field, fieldState }) => (
            <TextField {...field} size="small" type="number" label="Amount" error={!!fieldState.error}
              inputProps={{ step: 0.01, min: 0 }} sx={{ width: 130 }} />
          )} />
        <Controller name="mode" control={control}
          render={({ field }) => (
            <TextField {...field} onChange={(e) => { field.onChange(e); setMeta(blankMeta()); setShowMetaErrors(false); }} select size="small" label="Mode" sx={{ width: 130 }}>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="CARD">Card</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </TextField>
          )} />
        <Controller name="referenceNo" control={control}
          render={({ field }) => <TextField {...field} size="small" label="Reference #" sx={{ width: 140 }} />} />
        <Button type="submit" variant="contained" size="small" startIcon={<PaymentIcon />}
          disabled={mutation.isPending} sx={{ height: 40 }}>
          Record
        </Button>
      </Stack>
      <Grid container spacing={1} sx={{ mt: 0.5 }}>
        <PaymentLegFields mode={watch('mode')} value={meta} onChange={(p) => setMeta((m) => ({ ...m, ...p }))} options={payOptions}
          isLoading={optionsLoading} isError={optionsError} showErrors={showMetaErrors} error={legMetaError(watch('mode'), meta, payOptions)} sm={4} />
      </Grid>
    </form>
  );
}

// D1: return one or more tagged lines from this invoice. Only tagged lines are selectable (a
// free-text line has no physical piece to take back — the server rejects it too, this just keeps
// the clerk from picking one). Already-returned lines are excluded from selection entirely.
function ReturnItemsForm({ invoiceId, items, alreadyReturnedIds, returns }: {
  invoiceId: string; items: any[]; alreadyReturnedIds: Set<string>; returns: any[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');

  // A material (Metal Vault) line is returned by weight and may come back in parts, so it stays returnable until every
  // gram sold has been returned; a tagged line is all-or-nothing.
  const returnedWt = new Map<string, number>();
  for (const r of returns) for (const ri of r.items ?? []) {
    if (ri.returnedWeight != null) returnedWt.set(ri.invoiceItemId, (returnedWt.get(ri.invoiceItemId) ?? 0) + Number(ri.returnedWeight));
  }
  const outstandingWt = (i: any) => Math.round((Number(i.netGoldWeight) * Number(i.quantity) - (returnedWt.get(i.id) ?? 0)) * 10000) / 10000;
  const returnable = items.filter((i) => (i.materialSale ? outstandingWt(i) > 0 : i.tagId && !alreadyReturnedIds.has(i.id)));
  const [weights, setWeights] = useState<Record<string, string>>({});

  const toggle = (id: string) => {
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const canIssueCreditNote = useAuthStore((s) => s.hasPermission('creditNote:create'));
  const [issueCreditNote, setIssueCreditNote] = useState(true);

  const mutation = useMutation({
    mutationFn: () => apiPost('/sales/returns', {
      invoiceId,
      items: selected.map((invoiceItemId) => {
        const line = items.find((x) => x.id === invoiceItemId);
        return line?.materialSale
          ? { invoiceItemId, weight: Number(weights[invoiceItemId] ?? outstandingWt(line)) }
          : { invoiceItemId };
      }),
      reason: reason || undefined,
      issueCreditNote: canIssueCreditNote && issueCreditNote,
    }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
      qc.invalidateQueries({ queryKey: ['sales-invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice-returns', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoice-credit-notes', invoiceId] });
      qc.invalidateQueries({ queryKey: ['sales-credit-notes'] });
      setSelected([]);
      setWeights({});
      setReason('');
      const hasCn = !!(res?.data?.creditNote || res?.creditNote);
      enqueueSnackbar(
        hasCn ? 'Return recorded and formal GST Credit Note issued' : 'Return recorded — stock restored',
        { variant: 'success' },
      );
    },
    onError: (e: any) => enqueueSnackbar(e.response?.data?.error ?? 'Failed to record return', { variant: 'error' }),
  });

  if (returnable.length === 0) return null;

  return (
    <Box>
      <Stack spacing={1}>
        {returnable.map((item) => (
          <Stack key={item.id} direction="row" alignItems="center" spacing={1}>
            <Checkbox size="small" checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>{item.productCode}</Typography>
            <Typography variant="body2" color="text.secondary">{item.description}</Typography>
            {item.materialSale && selected.includes(item.id) && (
              <TextField size="small" type="number" label="Weight (g)" sx={{ width: 120 }}
                value={weights[item.id] ?? String(outstandingWt(item))}
                onChange={(e) => setWeights((p) => ({ ...p, [item.id]: e.target.value }))}
                inputProps={{ min: 0, max: outstandingWt(item), step: 0.001 }} />
            )}
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fmt(Number(item.totalPrice))}</Typography>
          </Stack>
        ))}
        {canIssueCreditNote && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={issueCreditNote}
                onChange={(e) => setIssueCreditNote(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                Also issue formal GST Credit Note
              </Typography>
            }
            sx={{ m: 0, pt: 0.5 }}
          />
        )}
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pt: 0.5 }}>
          <TextField size="small" label="Reason (optional)" value={reason}
            onChange={(e) => setReason(e.target.value)} sx={{ flex: 1 }} />
          <Button variant="contained" color="warning" size="small" startIcon={<Undo />}
            disabled={selected.length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()} sx={{ height: 40 }}>
            Return Selected
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function InvoiceDrawer({ invoiceId, onClose, onEdit }: { invoiceId: string; onClose: () => void; onEdit?: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const canCreateCN = useAuthStore((s) => s.hasPermission('creditNote:create'));
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['invoice-detail', invoiceId],
    queryFn: () => apiGet<any>(`/sales/invoices/${invoiceId}`),
    enabled: !!invoiceId,
  });
  const invoice = data;

  const { data: returnsData } = useQuery({
    queryKey: ['invoice-returns', invoiceId],
    queryFn: () => apiGet<any[]>('/sales/returns', { invoiceId }),
    enabled: !!invoiceId,
  });
  const returns: any[] = returnsData ?? [];
  const returnedItemIds = new Set(returns.flatMap((r) => r.items.map((i: any) => i.invoiceItemId)));

  const { data: creditNotesData } = useQuery({
    queryKey: ['invoice-credit-notes', invoiceId],
    queryFn: () => apiGet<any[]>('/sales/credit-notes', { invoiceId }),
    enabled: !!invoiceId,
  });
  const creditNotes: any[] = creditNotesData ?? [];

  const issueCnForReturnMutation = useMutation({
    mutationFn: (returnId: string) => apiPost(`/sales/returns/${returnId}/credit-note`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-credit-notes', invoiceId] });
      qc.invalidateQueries({ queryKey: ['sales-credit-notes'] });
      enqueueSnackbar('GST Credit Note issued for return', { variant: 'success' });
    },
    onError: (err: any) => {
      enqueueSnackbar(err.response?.data?.error ?? 'Failed to issue credit note', { variant: 'error' });
    },
  });

  return (
    <Drawer anchor="right" open={!!invoiceId} onClose={onClose} PaperProps={{ sx: { width: 640 } }}>
      <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Sales Invoice</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {invoice && onEdit && (
              <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
                Edit
              </Button>
            )}
            {invoice && (
              <Button size="small" startIcon={<Print />} onClick={() => printInvoice(invoice)}>
                Print
              </Button>
            )}
            {/* Delete bill - its own permission (invoice:delete), assignable in Roles & Permissions. The server re-checks it. */}
            {invoice && invoice.status !== 'VOID' && (
              <PermissionGate permission="invoice:delete">
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              </PermissionGate>
            )}
            <IconButton onClick={onClose}><Close /></IconButton>
          </Stack>
        </Stack>

        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

        {invoice && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                <Typography fontWeight={900} fontFamily="monospace" color="#44C47A">{invoice.invoiceNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><StatusChip status={invoice.status} /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Client</Typography>
                <Typography fontWeight={700}>{invoice.client?.name}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography>{invoice.invoiceType}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography>{new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Sales Executive</Typography>
                <Typography>{invoice.salesPerson?.name ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Terms</Typography>
                <Typography>{invoice.terms ?? '—'}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Items</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Tag / Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Wt (g)</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoice.items ?? []).map((item: any) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: item.tagId ? '#44C47A' : 'inherit' }}>
                      {item.productCode}
                      {returnedItemIds.has(item.id) && (
                        <Chip label="Returned" size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
                      )}
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.category?.name ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{Number(item.netGoldWeight).toFixed(3)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(Number(item.totalPrice))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <PermissionGate permission="return:create">
              <ReturnItemsForm invoiceId={invoice.id} items={invoice.items ?? []} alreadyReturnedIds={returnedItemIds} returns={returns} />
            </PermissionGate>

            {returns.length > 0 && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Returns</Typography>
                <Table size="small">
                  <TableBody>
                    {returns.map((r: any) => {
                      const hasCn = creditNotes.some((cn: any) => cn.salesReturnId === r.id);
                      return (
                        <TableRow key={r.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.returnNumber}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{new Date(r.returnDate).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.reason || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'warning.main' }}>
                            {fmt(Number(r.totalAmount))}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 95 }}>
                            {hasCn ? (
                              <Chip label="CN Issued" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                            ) : canCreateCN ? (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 10, py: 0.2, height: 22 }}
                                disabled={issueCnForReturnMutation.isPending}
                                onClick={() => issueCnForReturnMutation.mutate(r.id)}
                              >
                                Issue CN
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {creditNotes.length > 0 && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Credit Notes</Typography>
                <Table size="small">
                  <TableBody>
                    {creditNotes.map((cn: any) => (
                      <TableRow key={cn.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: cn.status === 'CANCELLED' ? 'text.secondary' : '#44C47A' }}>
                          {cn.creditNoteNumber}
                          {cn.status === 'CANCELLED' && (
                            <Chip label="Cancelled" size="small" color="error" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{new Date(cn.creditNoteDate).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: cn.status === 'CANCELLED' ? 'text.secondary' : 'error.main' }}>
                          - {fmt(Number(cn.totalAmount))}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 40 }}>
                          <Tooltip title="Print / PDF">
                            <IconButton size="small" onClick={() => printCreditNote(cn)}>
                              <Print fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {invoice.taxDetails && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>GST</Typography>
                <Stack spacing={0.5} sx={{ fontSize: 13 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Place of Supply</Typography>
                    <Typography variant="body2">{invoice.taxDetails.placeOfSupply || '—'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">CGST + SGST</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {fmt(Number(invoice.taxDetails.cgst) + Number(invoice.taxDetails.sgst))}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">IGST</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fmt(Number(invoice.taxDetails.igst))}</Typography>
                  </Stack>
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Total</Typography>
                <Typography fontWeight={700} sx={{ fontFamily: 'monospace' }}>{fmt(Number(invoice.totalAmount))}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Paid</Typography>
                <Typography sx={{ fontFamily: 'monospace', color: 'success.main' }}>{fmt(Number(invoice.paidAmount))}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Balance Due</Typography>
                <Typography fontWeight={700} sx={{ fontFamily: 'monospace', color: Number(invoice.balanceDue) > 0 ? 'error.main' : 'success.main' }}>
                  {fmt(Number(invoice.balanceDue))}
                </Typography>
              </Stack>
            </Stack>

            {(invoice.payments ?? []).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Payments</Typography>
                <Table size="small">
                  <TableBody>
                    {invoice.payments.map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell sx={{ fontSize: 12 }}>{new Date(p.paymentDate).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell><Chip label={p.mode} size="small" /></TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(Number(p.amount))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {Number(invoice.balanceDue) > 0 && (
              <PermissionGate permission="payment:create">
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Record Payment</Typography>
                <RecordPaymentForm invoiceId={invoice.id} balanceDue={Number(invoice.balanceDue)} branchId={invoice.branchId} />
              </PermissionGate>
            )}
          </>
        )}
      </Box>
      <DeleteBillDialog invoiceId={invoiceId} open={deleting} onClose={() => setDeleting(false)} onDeleted={onClose} />
    </Drawer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function SalesInvoices() {
  const navigate = useNavigate();
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  // Editing an issued invoice is its own permission (SUPER_ADMIN, or a role that was granted it) — the server enforces it too.
  const canEdit = useAuthStore((s) => s.hasPermission('invoice:edit'));
  const canCreateInvoice = useAuthStore((s) => s.hasPermission('invoice:create'));
  const canDelete = useAuthStore((s) => s.hasPermission('invoice:delete'));
  const [deleteId, setDeleteId] = useState('');
  const editInvoice = (id: string) => navigate(`/sales/invoices/${id}/edit`);
  // Invoice-date range (both ends inclusive), sent to the server. Empty = the latest 50; a range lifts that cap.
  const [from, setFrom] = useState(formatLocalDate()); // defaults to today
  const [to, setTo] = useState(formatLocalDate()); // defaults to today
  const rangeBad = !!from && !!to && from > to;

  const { data, isLoading } = useQuery({
    queryKey: ['sales-invoices', from, to],
    queryFn: () => apiGet<any[]>('/sales/invoices', { ...(from && { from }), ...(to && { to }) }),
    enabled: !rangeBad,
  });
  const money = (n: number) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const isoDay = (d: string) => new Date(d).toISOString().slice(0, 10);
  const wt = (n: unknown) => Number(n ?? 0).toFixed(3);

  return (
    <Box>
      <PageHeader title="Sales Invoices" />

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <DateField size="small" fullWidth={false} label="From" value={from} sx={{ width: 160 }}
          onChange={(e) => setFrom(e.target.value)} />
        <DateField size="small" fullWidth={false} label="To" value={to} sx={{ width: 160 }}
          error={rangeBad} helperText={rangeBad ? 'Must not be before From' : undefined}
          onChange={(e) => setTo(e.target.value)} />
        {(from || to) && <Button size="small" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</Button>}
        {!from && !to && <Typography variant="caption" color="text.secondary">Showing the latest 50 — pick dates to see older invoices</Typography>}
        {/* On the filter row (right-aligned) rather than in the header, where a lone button cost a whole line. */}
        <Box sx={{ ml: 'auto' }}>
          <PageActions actions={canCreateInvoice ? [{ label: 'New Sale', icon: <Add />, onClick: () => navigate('/sales/invoices/new') }] : []} />
        </Box>
      </Stack>

      <DataTable
        tableId="sales-invoices"
        rows={rangeBad ? [] : data ?? []}
        isLoading={isLoading && !rangeBad}
        getRowId={(inv: any) => inv.id}
        onRowClick={(inv: any) => setSelectedInvoiceId(inv.id)}
        emptyLabel="No sales invoices found"
        columns={[
          { key: 'invoiceNumber', label: 'Invoice #', width: 150, render: (inv: any) => (
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#44C47A', fontSize: 12 }}>{inv.invoiceNumber}</Typography>) },
          { key: 'client', label: 'Client', width: 200, filterValue: (inv: any) => inv.client?.name ?? '',
            render: (inv: any) => <Typography fontWeight={600} noWrap>{inv.client?.name}</Typography> },
          { key: 'invoiceDate', label: 'Date', width: 110, filterValue: (inv: any) => isoDay(inv.invoiceDate),
            render: (inv: any) => <Typography sx={{ fontSize: 12 }}>{new Date(inv.invoiceDate).toLocaleDateString('en-IN')}</Typography> },
          { key: 'invoiceType', label: 'Type', width: 110, render: (inv: any) => <Chip label={inv.invoiceType} size="small" /> },
          { key: 'currency', label: 'Currency', width: 100,
            render: (inv: any) => <Chip label={inv.currency} size="small" color={inv.currency !== 'INR' ? 'info' : 'default'} /> },
          { key: 'totalAmount', label: 'Total', align: 'right', width: 130, filterValue: (inv: any) => money(inv.totalAmount),
            render: (inv: any) => <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{inv.currency === 'INR' ? '₹' : '$'}{money(inv.totalAmount)}</Typography> },
          { key: 'paidAmount', label: 'Paid', align: 'right', width: 120, filterValue: (inv: any) => money(inv.paidAmount),
            render: (inv: any) => <Typography sx={{ fontFamily: 'monospace', color: 'success.main' }}>{money(inv.paidAmount)}</Typography> },
          { key: 'balanceDue', label: 'Balance', align: 'right', width: 120, filterValue: (inv: any) => money(inv.balanceDue),
            render: (inv: any) => (
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: Number(inv.balanceDue) > 0 ? 'error.main' : 'success.main' }}>
                {money(inv.balanceDue)}
              </Typography>) },
          { key: 'status', label: 'Status', width: 130, render: (inv: any) => <StatusChip status={inv.status} /> },

          // ── Optional columns — hidden until chosen from the Columns menu (button under the table, or right-click a header) ──
          { key: 'salesExecutive', label: 'Sales Executive', width: 160, defaultHidden: true,
            filterValue: (inv: any) => inv.salesPerson?.name ?? '—', render: (inv: any) => <Typography noWrap>{inv.salesPerson?.name ?? '—'}</Typography> },
          { key: 'branch', label: 'Branch', width: 140, defaultHidden: true,
            filterValue: (inv: any) => inv.branch?.name ?? '—', render: (inv: any) => <Typography noWrap>{inv.branch?.name ?? '—'}</Typography> },
          { key: 'clientCode', label: 'Client Code', width: 120, defaultHidden: true,
            filterValue: (inv: any) => inv.client?.clientCode ?? '', render: (inv: any) => <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{inv.client?.clientCode}</Typography> },
          { key: 'items', label: 'Lines', align: 'right', width: 80, defaultHidden: true,
            filterValue: (inv: any) => String(inv._count?.items ?? 0), render: (inv: any) => inv._count?.items ?? 0 },
          { key: 'qty', label: 'Qty', align: 'right', width: 80, defaultHidden: true,
            filterValue: (inv: any) => String(inv.totals?.quantity ?? 0), render: (inv: any) => inv.totals?.quantity ?? 0 },
          { key: 'grossWt', label: 'Gross Wt (g)', align: 'right', width: 120, defaultHidden: true,
            filterValue: (inv: any) => wt(inv.totals?.grossWeight), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{wt(inv.totals?.grossWeight)}</Typography> },
          { key: 'netWt', label: 'Net Wt (g)', align: 'right', width: 120, defaultHidden: true,
            filterValue: (inv: any) => wt(inv.totals?.netWeight), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{wt(inv.totals?.netWeight)}</Typography> },
          { key: 'stoneWt', label: 'Stone Wt (g)', align: 'right', width: 120, defaultHidden: true,
            filterValue: (inv: any) => wt(inv.totals?.stoneWeight), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{wt(inv.totals?.stoneWeight)}</Typography> },
          { key: 'subtotal', label: 'Subtotal', align: 'right', width: 120, defaultHidden: true,
            filterValue: (inv: any) => money(inv.subtotal), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.subtotal)}</Typography> },
          { key: 'discount', label: 'Discount', align: 'right', width: 110, defaultHidden: true,
            filterValue: (inv: any) => money(inv.discountAmount), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.discountAmount)}</Typography> },
          { key: 'otherCharges', label: 'Other Charges', align: 'right', width: 130, defaultHidden: true,
            filterValue: (inv: any) => money(inv.otherCharges), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.otherCharges)}</Typography> },
          { key: 'taxable', label: 'Taxable', align: 'right', width: 120, defaultHidden: true,
            filterValue: (inv: any) => money(inv.taxDetails?.taxableAmount ?? 0), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.taxDetails?.taxableAmount ?? 0)}</Typography> },
          { key: 'cgst', label: 'CGST', align: 'right', width: 100, defaultHidden: true,
            filterValue: (inv: any) => money(inv.taxDetails?.cgst ?? 0), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.taxDetails?.cgst ?? 0)}</Typography> },
          { key: 'sgst', label: 'SGST', align: 'right', width: 100, defaultHidden: true,
            filterValue: (inv: any) => money(inv.taxDetails?.sgst ?? 0), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.taxDetails?.sgst ?? 0)}</Typography> },
          { key: 'igst', label: 'IGST', align: 'right', width: 100, defaultHidden: true,
            filterValue: (inv: any) => money(inv.taxDetails?.igst ?? 0), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.taxDetails?.igst ?? 0)}</Typography> },
          { key: 'gstTotal', label: 'Total GST', align: 'right', width: 110, defaultHidden: true,
            filterValue: (inv: any) => money(inv.taxAmount), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.taxAmount)}</Typography> },
          { key: 'roundOff', label: 'Round Off', align: 'right', width: 100, defaultHidden: true,
            filterValue: (inv: any) => money(inv.roundOff), render: (inv: any) => <Typography sx={{ fontFamily: 'monospace' }}>{money(inv.roundOff)}</Typography> },
          { key: 'placeOfSupply', label: 'Place of Supply', width: 130, defaultHidden: true,
            filterValue: (inv: any) => inv.taxDetails?.placeOfSupply || '—', render: (inv: any) => inv.taxDetails?.placeOfSupply || '—' },
          { key: 'paymentModes', label: 'Paid By', width: 160, defaultHidden: true,
            filterValue: (inv: any) => (inv.paymentModes ?? []).join(', ') || '—',
            render: (inv: any) => <Typography noWrap fontSize={12}>{(inv.paymentModes ?? []).join(', ') || '—'}</Typography> },
          { key: 'terms', label: 'Terms', width: 110, defaultHidden: true,
            filterValue: (inv: any) => inv.terms ?? '—', render: (inv: any) => inv.terms ?? '—' },
          { key: 'dueDate', label: 'Due Date', width: 110, defaultHidden: true,
            filterValue: (inv: any) => (inv.dueDate ? isoDay(inv.dueDate) : '—'),
            render: (inv: any) => <Typography sx={{ fontSize: 12 }}>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-IN') : '—'}</Typography> },
          { key: 'notes', label: 'Notes', width: 200, defaultHidden: true,
            filterValue: (inv: any) => inv.notes ?? '', render: (inv: any) => <Typography noWrap fontSize={12} title={inv.notes ?? ''}>{inv.notes || '—'}</Typography> },
          { key: 'createdAt', label: 'Created', width: 150, defaultHidden: true,
            filterValue: (inv: any) => isoDay(inv.createdAt),
            render: (inv: any) => <Typography sx={{ fontSize: 12 }}>{new Date(inv.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</Typography> },
          ...(canEdit || canDelete ? [{
            key: 'actions', label: 'Actions', width: canEdit && canDelete ? 100 : 80, hideable: false, filterable: false, align: 'center' as const,
            render: (inv: any) => (
              <Stack direction="row" justifyContent="center">
                {canEdit && (
                  <Tooltip title="Edit invoice">
                    <IconButton size="small" aria-label={`Edit invoice ${inv.invoiceNumber}`}
                      onClick={(e) => { e.stopPropagation(); editInvoice(inv.id); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && inv.status !== 'VOID' && (
                  <Tooltip title="Delete bill">
                    <IconButton size="small" color="error" aria-label={`Delete invoice ${inv.invoiceNumber}`}
                      onClick={(e) => { e.stopPropagation(); setDeleteId(inv.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ),
          }] : []),
        ]}
      />

      {selectedInvoiceId && <InvoiceDrawer invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId('')}
        onEdit={canEdit ? () => editInvoice(selectedInvoiceId) : undefined} />}
      {deleteId && <DeleteBillDialog invoiceId={deleteId} open onClose={() => setDeleteId('')} />}
    </Box>
  );
}
